import React, { createContext, useContext, useEffect, useState } from "react";
import {
  User as FirebaseUser,
  UserCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth, initializeRemoteConfig } from "../services/firebase";
import { initializeRevenueCat } from "../services/revenuecat";
import { RevenueCatSubscriber } from "../types/revenuecat";
import { updateIntercomUser, resetToAnonymous } from "../services/intercom";
import {
  setBranchIdentity,
  clearBranchIdentity,
} from "../services/branch";
import {
  createUserDocument,
} from "../services/dealRepository";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { User } from "../types/user";

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userDocData: User | null;
  /**
   * Subscriber returned by the RevenueCat init call made on sign-in. Consumers
   * should read this instead of refetching it — revenueCatInitialize and
   * revenueCatGetCustomerInfo return the same payload.
   */
  subscriber: RevenueCatSubscriber | null;
  login: (email: string, password: string) => Promise<UserCredential>;
  signup: (email: string, password: string) => Promise<UserCredential>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<UserCredential>;
  loginWithApple: () => Promise<UserCredential>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userDocData, setUserDocData] = useState<User | null>(null);
  const [subscriber, setSubscriber] = useState<RevenueCatSubscriber | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize Remote Config on mount
  useEffect(() => {
    initializeRemoteConfig();
  }, []);

  const login = async (email: string, password: string) => {
    return await signInWithEmailAndPassword(auth, email, password);
  };

  const signup = async (email: string, password: string) => {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );

    // Create user document in Firestore
    if (userCredential.user) {
      await createUserDocument(
        userCredential.user.uid,
        userCredential.user.email
      );
    }

    return userCredential;
  };

  const logout = async () => {
    // Reset Intercom to anonymous mode instead of shutting down
    // This allows users to get help even after logging out
    resetToAnonymous();

    // Clear Branch identity
    clearBranchIdentity();

    await signOut(auth);
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);

    // Create user document if it's a new user
    if (userCredential.user) {
      await createUserDocument(
        userCredential.user.uid,
        userCredential.user.email
      );
    }

    return userCredential;
  };

  const loginWithApple = async () => {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    const userCredential = await signInWithPopup(auth, provider);

    // Create user document if it's a new user
    if (userCredential.user) {
      await createUserDocument(
        userCredential.user.uid,
        userCredential.user.email
      );
    }

    return userCredential;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Initialize/Login user in RevenueCat when user logs in
      if (user) {
        setSubscriber(
          await initializeRevenueCat(user.uid, user.email || undefined)
        );

        // Update Intercom with user information (for authenticated support)
        // Note: Intercom is already initialized in App.tsx for anonymous users
        updateIntercomUser({
          userId: user.uid,
          email: user.email || undefined,
          name: user.displayName || undefined,
        });

        // Set Branch identity for attribution
        setBranchIdentity(user.uid);

        // Pull user document from Firestore
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnapshot = await getDoc(userDocRef);

          if (userDocSnapshot.exists()) {
            console.log(
              "[Auth] User document fetched successfully:",
              userDocSnapshot.data()
            );
            setUserDocData(userDocSnapshot.data() as User);
          } else {
            console.log("[Auth] User document does not exist, creating one");
            await createUserDocument(user.uid, user.email);
            setUserDocData({
              id: user.uid,
              email: user.email || "",
              created_at: new Date().toISOString(),
              onboarding: {},
            } as User);
          }
        } catch (error) {
          console.error("[Auth] Error pulling user document:", error);
        }

        // Pending deals are no longer saved to user profile on login
        // They are only displayed in the UI when user navigates to Offers
      } else {
        setSubscriber(null);
        setUserDocData(null);
      }

      setLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    currentUser,
    userDocData,
    subscriber,
    login,
    signup,
    logout,
    loginWithGoogle,
    loginWithApple,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
